import clsx from 'clsx';
import React from 'react';
import { AiOutlineMinusCircle, AiOutlinePlusCircle } from 'react-icons/ai';
import { toast } from 'react-toastify';

import { useAgentEdit } from '@/app/agents/edit/state/agentEditContext';
import { getNodeTypeLabel } from '@/app/agents/edit/util/nodeTypeUtils';
import { validateVariableName } from '@/app/agents/edit/util/validation';
import { Text } from '@/components/typography/text';
import {
  AgentConfigStepInputs,
  AgentConfigStepTypeEnum,
  CONTEXT_INPUT_TYPES,
  ContextInputType
} from '@/types/agent';

import { getNextNewKey } from '../../util/getNextNewKey';


const ENTITY = 'input';

const LOCKED_INPUTS = [
  'conversation'
];

type Props = {
  editable?: boolean;
  inputs: AgentConfigStepInputs;
  onInputsChange: ( inputs: AgentConfigStepInputs ) => void;
  stepId: string;
  stepType: AgentConfigStepTypeEnum;
};

export default function PaneInputs( props: Props ) {
  const {
    editable = false,
    inputs,
    onInputsChange,
    stepId,
    stepType
  } = props;

  const { validationErrors } = useAgentEdit();
  const stepErrors = validationErrors[stepId]?.inputs || {};

  const onAddInputClick = () => {
    const newInputKey = getNextNewKey( Object.keys( inputs ), ENTITY );

    const nextInputs = {
      ...inputs,
      [ newInputKey ]: { type: 'STRING' as ContextInputType }
    };

    onInputsChange( nextInputs );
  };

  const onDeleteClick = ( key: string ) => {
    const { [ key ]: _, ...nextInputs } = inputs;

    onInputsChange( nextInputs );
  };

  const onKeyChange = ( e: React.FormEvent<HTMLInputElement>, oldKey: string ) => {
    const newKey = e.currentTarget.value;

    // Update the input value
    const nextInputs: AgentConfigStepInputs = {};
    for ( const k of Object.keys( inputs ) ) {
      nextInputs[ k === oldKey ? newKey : k ] = inputs[ k ];
    }
    onInputsChange( nextInputs );

    // Show toast for immediate feedback if invalid
    const error = validateVariableName( newKey );
    if ( error ) {
      toast.error( error.message );
    }
  };

  const onValueChange = ( e: React.FormEvent<HTMLSelectElement>, key: string ) => {
    const nextInputs = {
      ...inputs,
      [ key ]: { type: e.currentTarget.value as ContextInputType }
    };

    onInputsChange( nextInputs );
  };

  const inputEntries = Object.entries( inputs ?? {} );

  return (
    <div className='configPane'>
      <Text as='div' className='paneTitle' size='md' weight='bold'>{ getNodeTypeLabel( stepType ) } Node Inputs</Text>
      {
        inputEntries.map(
          ( [ k, v ], i ) => {
            const hasError = !!stepErrors[k];
            const isStartNode = stepType === AgentConfigStepTypeEnum.START;
            const errorMessage = hasError
              ? ( isStartNode ? stepErrors[k] : 'Check upstream node outputs to fix this error' )
              : undefined;

            return (
              <div className='inputRow' key={ i }>
                {
                  editable && !LOCKED_INPUTS.includes( k ) && (
                    <AiOutlineMinusCircle
                      className='inputIcon'
                      data-testid={ `config-edit-panel-inputs-input-delete-${ i }` }
                      onClick={ () => onDeleteClick( k ) }
                    />
                  )
                }
                <div
                  className={ clsx( 'inputName', { error: hasError } ) }
                  data-error-message={ errorMessage }
                >
                  <input
                    className={ clsx( { required: hasError } ) }
                    data-testid={ `config-edit-panel-inputs-input-${ i }-key` }
                    disabled={ !editable || LOCKED_INPUTS.includes( k ) }
                    onInput={ e => onKeyChange( e, k ) }
                    type='text'
                    value={ k }
                  />
                </div>
                {
                  !LOCKED_INPUTS.includes( k ) && (
                    <div className='inputValue'>
                      <select
                        data-testid={ `config-edit-panel-inputs-input-${ i }-value` }
                        value={ v.type }
                        onChange={ e => onValueChange( e, k ) }
                        disabled={ !editable }
                      >
                        {
                          ( Object.entries( CONTEXT_INPUT_TYPES ) as Array<[ ContextInputType, string ]> ).map(
                            ( [ type, label ] ) => (
                              <option key={ type } value={ type }>{ label }</option>
                            )
                          )
                        }
                      </select>
                    </div>
                  )
                }
              </div>
            );
          }
        )
      }
      {
        !inputEntries.length && (
          <div className='inputRow'>
            none
          </div>
        )
      }
      {
        editable && (
          <div
            className='addInputRow'
            onClick={ onAddInputClick }
            data-testid='config-edit-panel-inputs-input-add-button'
          >
            <AiOutlinePlusCircle className='button' />
            <Text as='span' size='sm'>Add input</Text>
          </div>
        )
      }
    </div>
  );
}
